import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile
} from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const storage = getStorage(app);

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

// Create new account with email and password
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update user profile with display name
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    
    return result.user;
  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Uploaded media filenames are unique (timestamp-prefixed), so objects never change
// under the same URL — cache them for a year and skip re-downloads (cuts Storage egress).
const DEFAULT_CACHE_CONTROL = 'public, max-age=31536000, immutable'; // 1 year

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = (e) => reject(e);
  reader.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = src;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), type, quality);
});

// Compress images client-side before uploading.
// This is used for profile / group photos to reduce size + speed up loads.
export const compressImageForUpload = async (file, opts = {}) => {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file; // keep animated GIFs intact

  const {
    maxDimension = 768,
    quality = 0.82
  } = opts;

  try {
    const dataUrl = await readFileAsDataUrl(file);
    const img = await loadImage(dataUrl);

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return file;

    const scale = Math.min(1, maxDimension / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, outW, outH);

    // Prefer WebP (keeps alpha + compresses well). Fall back to JPEG.
    let blob = await canvasToBlob(canvas, 'image/webp', quality);
    let outType = 'image/webp';
    if (!blob) {
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      outType = 'image/jpeg';
    }
    if (!blob) return file;

    // Only replace if smaller (otherwise keep original).
    if (blob.size >= file.size) return file;

    const ext = outType === 'image/webp' ? 'webp' : 'jpg';
    const base = (file.name || 'image').replace(/\.[^.]+$/, '');
    const outName = `${base}.${ext}`;
    return new File([blob], outName, { type: outType, lastModified: Date.now() });
  } catch (_) {
    return file;
  }
};

// FFmpeg.wasm instance (lazy-loaded on first video compression)
let ffmpegInstance = null;
let ffmpegLoading = false;

const getFFmpeg = async () => {
  if (ffmpegInstance && ffmpegInstance.loaded) return ffmpegInstance;
  if (ffmpegLoading) {
    // Wait for existing load to finish
    while (ffmpegLoading) {
      await new Promise(r => setTimeout(r, 100));
    }
    return ffmpegInstance;
  }
  ffmpegLoading = true;
  try {
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');
    const ffmpeg = new FFmpeg();
    // Load from CDN with CORS-safe blob URLs
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegInstance = ffmpeg;
    return ffmpeg;
  } catch (err) {
    console.error('Failed to load FFmpeg.wasm:', err);
    throw err;
  } finally {
    ffmpegLoading = false;
  }
};

// Find an ASCII marker (e.g. a fourcc like 'hvc1') inside a byte array.
const bytesContainAscii = (bytes, marker) => {
  const m = marker.split('').map((c) => c.charCodeAt(0));
  outer: for (let i = 0; i + m.length <= bytes.length; i++) {
    for (let j = 0; j < m.length; j++) {
      if (bytes[i + j] !== m[j]) continue outer;
    }
    return true;
  }
  return false;
};

// Decide whether a video will play in a *desktop* browser as-is.
// Desktop Chrome/Firefox cannot decode HEVC/H.265 and are unreliable with
// .mov/quicktime — both of which phones (especially iPhone) commonly produce.
// The Android app decodes these natively, so such clips play on the APK but
// show a dead control bar on desktop web. When a video isn't web-safe we must
// transcode it to H.264/AAC MP4 regardless of size, or it stays unplayable.
const isWebSafeVideo = async (file) => {
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';

  // Only mp4/webm containers are reliably web-playable. Everything else
  // (.mov/quicktime, .avi, .mkv, .3gp, .ts, or an unknown/empty type) → transcode.
  const containerOk =
    type === 'video/mp4' || type === 'video/webm' ||
    (!type && (ext === 'mp4' || ext === 'm4v' || ext === 'webm'));
  if (!containerOk) return false;

  // Even inside an mp4, the codec may be HEVC. Scan the file for the HEVC
  // sample-entry fourccs ('hvc1'/'hev1'); H.264 uses 'avc1'. The codec info
  // lives in the 'moov' box, which can be at the start (faststart) or the end,
  // so we sniff bounded head + tail slices instead of loading the whole file.
  try {
    const HEAD = Math.min(file.size, 8 * 1024 * 1024);
    const TAIL = Math.min(Math.max(file.size - HEAD, 0), 2 * 1024 * 1024);
    const head = new Uint8Array(await file.slice(0, HEAD).arrayBuffer());
    const tail = TAIL > 0 ? new Uint8Array(await file.slice(file.size - TAIL).arrayBuffer()) : new Uint8Array(0);
    for (const bytes of [head, tail]) {
      if (bytesContainAscii(bytes, 'hvc1') || bytesContainAscii(bytes, 'hev1')) return false;
      if (ext !== 'webm' && bytesContainAscii(bytes, 'av01')) return false; // AV1-in-mp4: transcode to be safe
    }
  } catch (_) {
    return false; // if we can't inspect it, transcode to be safe
  }
  return true;
};

// Compress video using FFmpeg.wasm (client-side transcoding)
const compressVideo = async (file, opts = {}) => {
  const { onProgress, sizeCompress = true } = opts;
  const fileSizeMB = file.size / (1024 * 1024);

  // Must-transcode when the source won't play in desktop browsers (HEVC/.mov/etc),
  // even if it's tiny — otherwise it plays on the Android app but not on the web.
  const mustTranscode = !(await isWebSafeVideo(file));

  // A web-safe file only needs a re-encode to save size, and only when the user
  // opted into compression (sizeCompress) and it's actually large. Otherwise keep
  // the original untouched (fast, lossless). Non-web-safe files always transcode.
  if (!mustTranscode && (!sizeCompress || fileSizeMB < 15)) {
    console.log(`🎬 Video ${fileSizeMB.toFixed(1)}MB already web-safe - skipping re-encode`);
    return file;
  }
  if (mustTranscode) {
    console.log(`🎬 Video not web-safe (HEVC/.mov/etc) - transcoding to H.264 for desktop playback`);
  }

  try {
    const ffmpeg = await getFFmpeg();
    const { fetchFile } = await import('@ffmpeg/util');

    // Adaptive quality tiers
    let crf, maxHeight;
    if (fileSizeMB < 25) {
      crf = '28'; maxHeight = 720;
    } else if (fileSizeMB < 100) {
      crf = '30'; maxHeight = 720;
    } else {
      crf = '32'; maxHeight = 480;
    }

    // Track progress during transcoding
    if (onProgress) {
      ffmpeg.on('progress', ({ progress }) => {
        onProgress(Math.round(progress * 100));
      });
    }

    const inputName = 'input' + (file.name.match(/\.[^.]+$/)?.[0] || '.mp4');
    const outputName = 'output.mp4';

    await ffmpeg.writeFile(inputName, await fetchFile(file));

    // Scale down to maxHeight while keeping aspect ratio, only if larger
    const scaleFilter = `scale=-2:'min(${maxHeight},ih)'`;

    await ffmpeg.exec([
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'fast',
      '-crf', crf,
      '-vf', scaleFilter,
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      '-y', outputName
    ]);

    const data = await ffmpeg.readFile(outputName);
    const blob = new Blob([data.buffer], { type: 'video/mp4' });

    // Clean up
    try {
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);
    } catch (_) {}

    // Only fall back to the original when we transcoded purely to save size.
    // If we HAD to transcode for compatibility, keep the H.264 output even if
    // it isn't smaller — the original wouldn't play on desktop at all.
    if (!mustTranscode && blob.size >= file.size) {
      console.log(`🎬 Compressed video not smaller, keeping original`);
      return file;
    }

    const base = (file.name || 'video').replace(/\.[^.]+$/, '');
    const compressed = new File([blob], `${base}_compressed.mp4`, { type: 'video/mp4', lastModified: Date.now() });
    console.log(`🎬 Video compressed: ${fileSizeMB.toFixed(1)}MB → ${(compressed.size / (1024 * 1024)).toFixed(1)}MB`);
    return compressed;
  } catch (err) {
    console.error('Video compression failed, using original:', err);
    return file;
  }
};

// Adaptive compression for chat media - adjusts based on file size and type
export const compressMediaForChat = async (file, opts = {}) => {
  if (!file || !file.type) return file;
  
  const {
    forceCompress = true, // If false, returns original (for "Full Res" option)
    maxDimension = 1920, // Max dimension for compressed images
    quality = 0.82
  } = opts;

  // Videos: always normalize to a desktop-playable codec, even in "Full Res"
  // mode. An HEVC/.mov clip left untranscoded plays on the Android app but
  // shows a dead control bar on desktop web. compressVideo() transcodes only
  // when the source isn't web-safe, and additionally shrinks large files when
  // the user opted into size compression (forceCompress).
  if (file.type.startsWith('video/')) {
    return compressVideo(file, { ...opts, sizeCompress: forceCompress });
  }

  // If user chose "Full Res", return original
  if (!forceCompress) return file;

  // For images (except GIFs), compress
  if (file.type.startsWith('image/')) {
    if (file.type === 'image/gif') return file; // Keep GIFs uncompressed
    
    // Adaptive quality based on file size
    const fileSizeMB = file.size / (1024 * 1024);
    let adaptiveQuality = quality;
    let adaptiveMaxDim = maxDimension;
    
    if (fileSizeMB < 0.5) {
      // Small files: light compression
      adaptiveQuality = 0.88;
      adaptiveMaxDim = 1920;
    } else if (fileSizeMB < 2) {
      // Medium files: moderate compression
      adaptiveQuality = 0.82;
      adaptiveMaxDim = 1920;
    } else {
      // Large files: aggressive compression
      adaptiveQuality = 0.75;
      adaptiveMaxDim = 1920;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const img = await loadImage(dataUrl);

      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return file;

      const scale = Math.min(1, adaptiveMaxDim / Math.max(w, h));
      const outW = Math.max(1, Math.round(w * scale));
      const outH = Math.max(1, Math.round(h * scale));

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) return file;
      ctx.drawImage(img, 0, 0, outW, outH);

      // Prefer WebP for better compression
      let blob = await canvasToBlob(canvas, 'image/webp', adaptiveQuality);
      let outType = 'image/webp';
      if (!blob) {
        blob = await canvasToBlob(canvas, 'image/jpeg', adaptiveQuality);
        outType = 'image/jpeg';
      }
      if (!blob) return file;

      // Only replace if smaller
      if (blob.size >= file.size) return file;

      const ext = outType === 'image/webp' ? 'webp' : 'jpg';
      const base = (file.name || 'image').replace(/\.[^.]+$/, '');
      const outName = `${base}_compressed.${ext}`;
      return new File([blob], outName, { type: outType, lastModified: Date.now() });
    } catch (_) {
      return file;
    }
  }

  // For other file types, return as-is
  return file;
};

export const uploadFile = async (file, path, options = {}) => {
  try {
    const storageRef = ref(storage, path);
    const metadata = {
      cacheControl: options.cacheControl || DEFAULT_CACHE_CONTROL
    };
    if (options.contentType) metadata.contentType = options.contentType;

    // Use resumable upload for progress tracking and network resilience
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    const snapshot = await new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snap) => {
          if (options.onProgress) {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            options.onProgress(pct);
          }
        },
        (error) => reject(error),
        () => resolve(uploadTask.snapshot)
      );
    });

    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export default app;

